import {
  WorkItemArea,
  WorkItemOrigin,
  WorkItemPriority,
  WorkItemSeverity,
  WorkItemStatus,
  WorkItemType,
} from '../interfaces/worklog.interface'

// Metadata visual del tracker interno. Colores alineados a los tokens de marca Rendafin
// ($success #00C781, $primary #202835, $danger #d21313) y a los colores de proyecto del
// roadmap (API #3B82F6 · Plataforma #8B5CF6 · Canal #EC4899).

/** Tipo de item: tarea (feature/mejora) vs bug. Icono + color. */
export const TYPE_META: Record<WorkItemType, { label: string; color: string; icon: string }> = {
  task: { label: 'Tarea', color: '#2563EB', icon: 'fa-solid fa-list-check' },
  bug: { label: 'Bug', color: '#d21313', icon: 'fa-solid fa-bug' },
}

/** Área / etiqueta de la implementación. */
export const AREA_META: Record<WorkItemArea, { label: string; short: string; color: string; icon: string }> = {
  backend: { label: 'Back', short: 'Back', color: '#3B82F6', icon: 'fa-solid fa-server' },
  frontend: { label: 'Front', short: 'Front', color: '#8B5CF6', icon: 'fa-solid fa-desktop' },
  channel: { label: 'Canal Digital', short: 'Canal', color: '#EC4899', icon: 'fa-solid fa-globe' },
  mobile: { label: 'Mobile', short: 'Mobile', color: '#06b6d4', icon: 'fa-solid fa-mobile-screen-button' },
}

export const AREA_ORDER: WorkItemArea[] = ['frontend', 'backend', 'channel', 'mobile']

type StatusMeta = { label: string; color: string; icon: string; order: number }

/**
 * Metadata visual por estado del ciclo de vida. `order` define la posición en el flujo.
 * `Cerrada` se lee «Terminada»: desde ADR-0065 significa liberada a producción.
 */
export const STATUS_META: Record<WorkItemStatus, StatusMeta> = {
  Backlog: { label: 'Backlog', color: '#94a3b8', icon: 'fa-solid fa-inbox', order: 0 },
  'Análisis': { label: 'Análisis', color: '#64748b', icon: 'fa-solid fa-magnifying-glass', order: 1 },
  Desarrollo: { label: 'Desarrollo', color: '#3B82F6', icon: 'fa-solid fa-code', order: 2 },
  QA: { label: 'QA', color: '#8B5CF6', icon: 'fa-solid fa-vial-circle-check', order: 3 },
  'To Deploy': { label: 'To Deploy', color: '#0ea5e9', icon: 'fa-solid fa-rocket', order: 4 },
  Cerrada: { label: 'Terminada', color: '#00C781', icon: 'fa-solid fa-circle-check', order: 5 },
  Cancelada: { label: 'Cancelada', color: '#d21313', icon: 'fa-solid fa-ban', order: 6 },
}

/**
 * Etapas retiradas que el historial conserva con su nombre de entonces. Solo sirven para pintar
 * filas viejas: ninguna tarea está hoy en ellas. `Deploy` significaba «en producción».
 */
const LEGACY_STATUS_META: Record<string, StatusMeta> = {
  'Auditoría': { label: 'Auditoría', color: '#f59e0b', icon: 'fa-solid fa-shield-halved', order: 3 },
  'Capacitación': { label: 'Capacitación', color: '#14b8a6', icon: 'fa-solid fa-chalkboard-user', order: 4 },
  Deploy: { label: 'Deploy', color: '#0ea5e9', icon: 'fa-solid fa-rocket', order: 5 },
}

/** Meta de cualquier estado, vigente o de una fila vieja del historial. */
export const statusMetaOf = (s: string): StatusMeta | undefined => (STATUS_META as Record<string, StatusMeta>)[s] ?? LEGACY_STATUS_META[s]

/**
 * Los estados de TRABAJO del Tablero, en el orden del flujo (ADR-0065): se desarrolla, se verifica
 * en QA y queda esperando el deploy. Es lo que cuenta como «en curso».
 */
export const BOARD_COLUMNS: WorkItemStatus[] = ['Análisis', 'Desarrollo', 'QA', 'To Deploy']

/**
 * Columna «Terminadas»: lo que el sprint ya liberó. Va al final del Tablero pero NO es trabajo en
 * curso, así que queda fuera de `BOARD_COLUMNS`.
 */
export const DONE_COLUMN: WorkItemStatus = 'Cerrada'

/** Prioridad de atención. */
export const PRIORITY_META: Record<WorkItemPriority, { label: string; color: string; icon: string }> = {
  low: { label: 'Baja', color: '#94a3b8', icon: 'fa-solid fa-flag' },
  medium: { label: 'Media', color: '#3B82F6', icon: 'fa-solid fa-flag' },
  high: { label: 'Alta', color: '#f59e0b', icon: 'fa-solid fa-flag' },
  urgent: { label: 'Urgente', color: '#d21313', icon: 'fa-solid fa-fire' },
}

export const PRIORITY_ORDER: WorkItemPriority[] = ['urgent', 'high', 'medium', 'low']

/** Severidad de bug (solo type='bug'). */
export const SEVERITY_META: Record<WorkItemSeverity, { label: string; color: string }> = {
  critical: { label: 'Crítico', color: '#b91c1c' },
  high: { label: 'Alto', color: '#d21313' },
  medium: { label: 'Medio', color: '#f59e0b' },
  low: { label: 'Bajo', color: '#3B82F6' },
}

/** Origen (continuidad con roadmap.json): solicitada vs extra proactivo del equipo. */
export const ORIGIN_META: Record<WorkItemOrigin, { label: string; color: string; icon: string }> = {
  requested: { label: 'Solicitada', color: '#64748b', icon: 'fa-solid fa-inbox' },
  proposed: { label: 'Extra', color: '#8B5CF6', icon: 'fa-solid fa-lightbulb' },
}

/**
 * Escala de esfuerzo (Fibonacci). La puntúa el equipo de TI al mandar la tarea a To Deploy, con
 * el costo real ya conocido — no quien reporta.
 *
 * Los tramos están calibrados con las 151 tareas puntuadas del histórico de roadmap.json, no
 * inventados: la mediana real es 3. El histórico traía valores libres (6, 7, 10, 11, 15, 21);
 * de aquí en adelante se estandariza a Fibonacci para que la velocidad por sprint sea
 * comparable. La guía con ejemplos reales vive en `.claude/worklog-puntuacion.md` del workspace.
 */
export const EFFORT_POINTS: { value: number; hint: string }[] = [
  { value: 1, hint: 'Ajuste puntual en una sola capa · < 2 h' },
  { value: 2, hint: 'Fix pequeño con su prueba · medio día' },
  { value: 3, hint: 'Pantalla o endpoint acotado · ~1 día' },
  { value: 5, hint: 'Feature en dos capas (API + UI) · 2-3 días' },
  { value: 8, hint: 'Rediseño de un módulo existente · ~1 semana' },
  { value: 13, hint: 'Módulo nuevo con varias piezas · 1-2 semanas' },
  { value: 21, hint: 'Núcleo nuevo cross-repo · varias semanas' },
]

/**
 * Etapa que exige la tarea clasificada — esfuerzo + áreas — al entrar (espejo de
 * `REQUIRES_POINTS_ON` del backend): To Deploy, que es cuando el desarrollo terminó de verdad.
 */
export const REQUIRES_POINTS_ON: WorkItemStatus = 'To Deploy'

/**
 * Pseudo-columna del Tablero (ADR-0040 · D8): comprometidas al sprint activo que aún no
 * arrancan. No es un estado de la máquina — al arrastrar desde/hacia ella se traduce a 'Backlog'.
 */
export const TODO_COLUMN = 'Por hacer'

/** Columnas visibles del Tablero: Por hacer + los estados de trabajo + Terminadas. */
export const BOARD_VIEW_COLUMNS: string[] = [TODO_COLUMN, ...BOARD_COLUMNS, DONE_COLUMN]

/**
 * Transiciones permitidas (espejo de api-rendafin work-item-status.enum.ts, V4 · ADR-0065): el
 * rechazo en QA o en To Deploy regresa a Desarrollo, y `To Deploy → Cerrada` es la liberación.
 */
export const WORK_ITEM_TRANSITIONS: Record<WorkItemStatus, WorkItemStatus[]> = {
  Backlog: ['Análisis', 'Cancelada'],
  'Análisis': ['Desarrollo', 'Backlog', 'Cancelada'],
  Desarrollo: ['QA', 'Análisis', 'Cancelada'],
  QA: ['To Deploy', 'Desarrollo', 'Cancelada'],
  'To Deploy': ['Cerrada', 'Desarrollo', 'Cancelada'],
  Cerrada: [],
  //* Una cancelación se puede deshacer: la tarea vuelve al Backlog con su folio e historia.
  Cancelada: ['Backlog'],
}
